package it.maicol07.gamerlogue.`data`

import it.maicol07.spraypaintkt.CollectionProxy
import it.maicol07.spraypaintkt.RecordProxy
import it.maicol07.spraypaintkt.Resource
import it.maicol07.spraypaintkt.ResourceRegistry
import it.maicol07.spraypaintkt.ResourceSerializer
import it.maicol07.spraypaintkt.Scope
import it.maicol07.spraypaintkt.SortDirection
import it.maicol07.spraypaintkt.extensions.DirtyMap
import it.maicol07.spraypaintkt.extensions.trackChanges
import it.maicol07.spraypaintkt.interfaces.JsonApiConfig
import it.maicol07.spraypaintkt.registerResources
import kotlin.Any
import kotlin.Boolean
import kotlin.Int
import kotlin.Number
import kotlin.String
import kotlin.Unit
import kotlin.collections.MutableMap
import kotlin.reflect.KClass
import kotlinx.serialization.Serializable

@Serializable(UserList.Companion.Serializer::class)
public class UserList : Resource, UserListSchema {
  override val `companion`: Resource.CompanionObj<UserList> = Companion

  override var id: String? = null

  override var isPersisted: Boolean = false

  override val attributes: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val relationships: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val meta: MutableMap<String, Any?> = mutableMapOf()

  override val links: MutableMap<String, Any?> = mutableMapOf()

  override val type: String by lazy { companion.resourceType }

  override var name: String
    get() = if (attributes.containsKey("name")) {
      attributes["name"] as String
    } else {
      throw NoSuchElementException("name not found")
    }
    set(`value`) {
      attributes["name"] = value
    }

  override var description: String?
    get() = if (attributes.containsKey("description")) {
      attributes["description"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["description"] = value
    }

  override var visibility: String
    get() = if (attributes.containsKey("visibility")) {
      attributes["visibility"] as String
    } else {
      super.visibility
    }
    set(`value`) {
      attributes["visibility"] = value
    }

  override var isRanked: Boolean
    get() = if (attributes.containsKey("is_ranked")) {
      attributes["is_ranked"] as Boolean
    } else {
      super.isRanked
    }
    set(`value`) {
      attributes["is_ranked"] = value
    }

  override var userId: String?
    get() = if (attributes.containsKey("user_id")) {
      attributes["user_id"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["user_id"] = value
    }

  override var itemsCount: Int
    get() = if (attributes.containsKey("items_count")) {
      attributes["items_count"] as Int
    } else {
      super.itemsCount
    }
    set(`value`) {
      attributes["items_count"] = value
    }

  override var likesCount: Int
    get() = if (attributes.containsKey("likes_count")) {
      attributes["likes_count"] as Int
    } else {
      super.likesCount
    }
    set(`value`) {
      attributes["likes_count"] = value
    }

  override var commentsCount: Int
    get() = if (attributes.containsKey("comments_count")) {
      attributes["comments_count"] as Int
    } else {
      super.commentsCount
    }
    set(`value`) {
      attributes["comments_count"] = value
    }

  override var likedByMe: Boolean
    get() = if (attributes.containsKey("liked_by_me")) {
      attributes["liked_by_me"] as Boolean
    } else {
      super.likedByMe
    }
    set(`value`) {
      attributes["liked_by_me"] = value
    }

  override var createdAt: String?
    get() = if (attributes.containsKey("created_at")) {
      attributes["created_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["created_at"] = value
    }

  override var updatedAt: String?
    get() = if (attributes.containsKey("updated_at")) {
      attributes["updated_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["updated_at"] = value
    }

  override var user: User?
    get() = if (relationships.containsKey("user") && (relationships["user"] == null || relationships["user"] is User?)) {
      relationships["user"] as User?
    } else {
      null
    }
    set(`value`) {
      relationships["user"] = value
    }

  public constructor(
    name: String? = null,
    description: String? = null,
    visibility: String? = null,
    isRanked: Boolean? = null,
    userId: String? = null,
    itemsCount: Int? = null,
    likesCount: Int? = null,
    commentsCount: Int? = null,
    likedByMe: Boolean? = null,
    createdAt: String? = null,
    updatedAt: String? = null,
    user: User? = null,
  ) {
    if (name != null) {
      attributes["name"] = name
    }
    if (description != null) {
      attributes["description"] = description
    }
    if (visibility != null) {
      attributes["visibility"] = visibility
    }
    if (isRanked != null) {
      attributes["is_ranked"] = isRanked
    }
    if (userId != null) {
      attributes["user_id"] = userId
    }
    if (itemsCount != null) {
      attributes["items_count"] = itemsCount
    }
    if (likesCount != null) {
      attributes["likes_count"] = likesCount
    }
    if (commentsCount != null) {
      attributes["comments_count"] = commentsCount
    }
    if (likedByMe != null) {
      attributes["liked_by_me"] = likedByMe
    }
    if (createdAt != null) {
      attributes["created_at"] = createdAt
    }
    if (updatedAt != null) {
      attributes["updated_at"] = updatedAt
    }
    if (user != null) {
      relationships["user"] = user
    }
  }

  public companion object : Resource.CompanionObj<UserList> {
    override val resourceType: String = "List"

    override val endpoint: String = "lists"

    override val config: JsonApiConfig = AppJsonApiConfig

    override val factory: () -> UserList = { UserList() }

    override val schema: KClass<UserListSchema> = UserListSchema::class

    init {
      ResourceRegistry.registerResources()
    }

    public fun scope(options: Scope<UserList>.() -> Unit = {}): Scope<UserList> = Scope(UserList::class, options)

    public final suspend fun all(): CollectionProxy<UserList> = scope().all()

    public final suspend fun find(id: String): RecordProxy<UserList> = scope().find(id)

    public final suspend fun findOrNull(id: String): RecordProxy<UserList?> = scope().findOrNull(id)

    public final suspend fun first(): RecordProxy<UserList> = scope().first()

    public final suspend fun firstOrNull(): RecordProxy<UserList?> = scope().firstOrNull()

    public final suspend fun exists(): Boolean = scope().exists()

    public final suspend fun last(): RecordProxy<UserList> = scope().last()

    public final suspend fun lastOrNull(): RecordProxy<UserList?> = scope().lastOrNull()

    public final fun page(pageNumber: Number): Scope<UserList> = scope().page(pageNumber)

    public final fun per(pageSize: Number): Scope<UserList> = scope().per(pageSize)

    public final fun limit(limit: Number): Scope<UserList> = scope().limit(limit)

    public final fun offset(offset: Number): Scope<UserList> = scope().offset(offset)

    public final fun `where`(attribute: String, `value`: Any): Scope<UserList> = scope().where(attribute, value)

    public final fun extraParam(key: String, `value`: String): Scope<UserList> = scope().extraParam(key, value)

    public final fun order(attribute: String): Scope<UserList> = scope().order(attribute)

    public final fun order(attribute: String, sortDirection: SortDirection): Scope<UserList> = scope().order(attribute, sortDirection)

    public final fun includes(vararg relationships: String): Scope<UserList> = scope().includes(*relationships)

    public final fun select(type: String, vararg fields: String): Scope<UserList> = scope().select(type, *fields)

    public object Serializer : ResourceSerializer<UserList>()
  }
}
